import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Zap, Flame, Home, Thermometer, Droplets, CookingPot, Plug,
  Receipt, ShieldCheck, ChevronLeft, ChevronRight, Send, Loader2, Share2, Check, Clock, ClipboardList, AlertTriangle, BadgeCheck,
  Save, Copy, Link2, RotateCcw, Mail, MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { LOGO_URL, shareFormWhatsApp } from "@/lib/brand";
import { toast } from "@/components/ui/sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  SectionHeader, Field, TextInput, TextArea, OptionGrid, Pills, PriceUpload,
} from "@/components/form/Controls";
import {
  ELEC_COMPTEUR, ELEC_OPTIONS, GAZ_TYPE, LOGEMENT_TYPE, ANNEE_CONSTRUCTION,
  PRESENCE, DPE_CONNU, DPE_CLASSES, CHAUFFAGE, EAU_CHAUDE, CUISSON, EQUIPEMENTS, RAPPEL_CRENEAUX,
  TYPE_DEMANDE, OUI_NON,
} from "@/lib/formConfig";

const STEPS = [
  { icon: ClipboardList, title: "Votre demande", subtitle: "En quelques secondes, dites-nous l'essentiel." },
  { icon: User, title: "Vos coordonnées", subtitle: "Pour que notre conseillère puisse vous rappeler." },
  { icon: Zap, title: "Compteur électricité", subtitle: "Les informations de votre contrat d'électricité." },
  { icon: Flame, title: "Compteur gaz", subtitle: "Si vous êtes également raccordé au gaz." },
  { icon: Home, title: "Votre logement", subtitle: "Quelques détails sur votre habitation." },
  { icon: Thermometer, title: "Chauffage", subtitle: "Comment chauffez-vous votre logement ?" },
  { icon: Droplets, title: "Eau chaude", subtitle: "Votre mode de production d'eau chaude." },
  { icon: CookingPot, title: "Cuisson", subtitle: "Vos équipements de cuisson." },
  { icon: Plug, title: "Équipements", subtitle: "Sélectionnez vos appareils principaux." },
  { icon: Receipt, title: "Prix & commentaires", subtitle: "Joignez une capture de vos prix actuels." },
  { icon: ShieldCheck, title: "Validation", subtitle: "Dernière étape avant l'envoi." },
];

const INITIAL = {
  type_demande: "", demande_urgente: "",
  apporteur: "", nom: "", prenom: "", adresse: "", code_postal_ville: "",
  telephone_fixe: "", telephone_portable: "", email: "", date_naissance: "",
  rappel_date: "", rappel_creneau: "",
  elec_compteur: "", elec_puissance: "", elec_pdl: "", elec_options: [], elec_fournisseur: "",
  gaz_type: [], gaz_fournisseur: "",
  logement_type: "", annee_construction: "", surface: "", nombre_occupants: "", presence: "",
  connait_dpe: "", dpe_classe: "",
  chauffage: [], eau_chaude: [], cuisson: [], equipements: [], equipements_autres: "",
  prix_fichier: null, commentaires: "", autorisation_donnees: false,
  licensee_code: "",
};

const DRAFT_STORAGE_KEY = "pde_draft";

const hasContact = (d) =>
  !!(d.nom?.trim() && d.prenom?.trim() && d.telephone_portable?.trim());

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

const buildFields = (d) => {
  const f = { ...d };
  delete f.prix_fichier;
  f.prix_fichier_path = d.prix_fichier?.path || null;
  f.prix_fichier_nom = d.prix_fichier?.filename || null;
  return f;
};

export default function LeadForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [refName, setRefName] = useState(null);
  const [resumeToken, setResumeToken] = useState(null);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [resumeOffer, setResumeOffer] = useState(null);
  const [savingLater, setSavingLater] = useState(false);
  const draftIdRef = useRef(null);
  const saveChain = useRef(Promise.resolve());

  const applyDraft = useCallback((payload) => {
    const f = payload.fields || {};
    const next = { ...INITIAL };
    Object.keys(INITIAL).forEach((k) => {
      if (f[k] !== undefined && f[k] !== null) next[k] = f[k];
    });
    if (f.prix_fichier_path) next.prix_fichier = { path: f.prix_fichier_path, filename: f.prix_fichier_nom };
    next.licensee_code = f.licensee_code || "";
    setData(next);
    draftIdRef.current = payload.id;
    setResumeToken(payload.resume_token);
    setStep(Math.min(payload.current_step || 0, STEPS.length - 1));
    setResumeOffer(null);
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ token: payload.resume_token }));
    if (f.licensee_code) {
      api.get(`/ref/${f.licensee_code}`).then((r) => { if (r.data?.name) setRefName(r.data.name); }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reprise = params.get("reprise");
    const ref = params.get("ref");

    if (reprise) {
      api.get(`/drafts/${reprise}`)
        .then((res) => applyDraft(res.data))
        .catch(() => toast.error("Ce lien de reprise n'est plus valide ou la demande a déjà été envoyée."));
      return;
    }

    if (ref) {
      // A shared licensee link should first show the landing page (présentation du formulaire).
      // Skip the landing only if the visitor already started from it, or has a draft in progress.
      const fromLanding = location.state?.fromLanding;
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!fromLanding && !savedDraft) {
        navigate(`/?ref=${encodeURIComponent(ref)}`, { replace: true });
        return;
      }
      setData((d) => ({ ...d, licensee_code: ref }));
      api.get(`/ref/${ref}`).then((res) => { if (res.data?.name) setRefName(res.data.name); }).catch(() => {});
      return;
    }

    const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (saved) {
      try {
        const { token } = JSON.parse(saved);
        if (token) {
          api.get(`/drafts/${token}`).then((r) => {
            const name = `${r.data.fields?.prenom || ""} ${r.data.fields?.nom || ""}`.trim();
            setResumeOffer({ token, name });
          }).catch(() => localStorage.removeItem(DRAFT_STORAGE_KEY));
        }
      } catch {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    }
  }, [applyDraft, location.state, navigate]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const total = STEPS.length;
  const progress = ((step + 1) / total) * 100;

  // Serialized so concurrent calls (autosave + navigation) never create duplicate drafts.
  const saveDraft = useCallback((stepIndex, silent = true) => {
    if (!hasContact(data)) return Promise.resolve(null);
    const fields = buildFields(data);
    const run = async () => {
      try {
        const res = await api.post("/drafts", {
          id: draftIdRef.current,
          current_step: stepIndex,
          fields,
        });
        draftIdRef.current = res.data.id;
        setResumeToken(res.data.resume_token);
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ token: res.data.resume_token }));
        return res.data;
      } catch {
        if (!silent) toast.error("Échec de l'enregistrement du brouillon.");
        return null;
      }
    };
    const p = saveChain.current.then(run, run);
    saveChain.current = p.then(() => {}, () => {});
    return p;
  }, [data]);

  // Auto-save a draft as soon as the contact info exists — even if the visitor
  // never clicks "Continuer" or leaves the email empty. This is what makes
  // abandoned forms show up in the back-office (Brouillons).
  useEffect(() => {
    if (!hasContact(data)) return;
    const t = setTimeout(() => { saveDraft(step); }, 1200);
    return () => clearTimeout(t);
  }, [data, step, saveDraft]);

  const canProceed = () => {
    if (step === 1) {
      return data.nom.trim() && data.prenom.trim() && data.telephone_portable.trim()
        && isValidEmail(data.email) && data.adresse.trim() && data.code_postal_ville.trim();
    }
    if (step === 9) {
      return !!data.prix_fichier?.path;
    }
    if (step === total - 1) {
      return data.autorisation_donnees;
    }
    return true;
  };

  const go = (next) => {
    setDir(next > step ? 1 : -1);
    setStep(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    if (!canProceed()) {
      if (step === 1) {
        if (data.email.trim() && !isValidEmail(data.email)) toast.error("Merci de saisir une adresse email valide.");
        else toast.error("Merci de renseigner nom, prénom, téléphone, email, adresse et code postal / ville.");
      }
      else if (step === 9) toast.error("Merci de joindre une photo / capture de votre facture.");
      return;
    }
    if (step < total - 1) {
      const next = step + 1;
      go(next);
      if (hasContact(data)) saveDraft(next);
    }
  };

  const handleSaveLater = async () => {
    if (!hasContact(data)) {
      toast.error("Renseignez d'abord vos nom, prénom et téléphone pour enregistrer votre saisie.");
      go(1);
      return;
    }
    setSavingLater(true);
    const res = await saveDraft(step, false);
    setSavingLater(false);
    if (res) setResumeDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!hasContact(data) || !isValidEmail(data.email) || !data.adresse.trim() || !data.code_postal_ville.trim()) {
      toast.error("Coordonnées incomplètes : nom, prénom, téléphone, email valide, adresse et code postal / ville requis.");
      go(1);
      return;
    }
    if (!data.prix_fichier?.path) {
      toast.error("Merci de joindre une photo / capture de votre facture avant l'envoi.");
      go(9);
      return;
    }
    if (!data.autorisation_donnees) {
      toast.error("Merci d'accepter l'accès aux données pour finaliser.");
      return;
    }
    setSubmitting(true);
    try {
      // Make sure the latest edits are persisted and the draft id is known before finalizing.
      await saveDraft(step, true);
      const payload = {
        ...data,
        prix_fichier_path: data.prix_fichier?.path || null,
        prix_fichier_nom: data.prix_fichier?.filename || null,
        draft_id: draftIdRef.current,
      };
      delete payload.prix_fichier;
      await api.post("/leads", payload);
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      navigate("/success");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Une erreur est survenue. Réessayez.");
      setSubmitting(false);
    }
  };

  const resumeLink = resumeToken ? `${window.location.origin}/formulaire?reprise=${resumeToken}` : "";

  const Current = STEPS[step];

  return (
    <div className="min-h-screen bg-slate-50 pde-grid-bg">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-[640px] items-center justify-between px-5 py-3.5">
          <Link to="/" data-testid="brand-logo-link">
            <img src={LOGO_URL} alt="Place des Énergies" className="h-12 w-auto sm:h-14" data-testid="brand-logo" />
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="save-later-button"
              onClick={handleSaveLater}
              disabled={savingLater}
              className="flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/[0.06] px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-60"
            >
              {savingLater ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">Reprendre plus tard</span>
              <span className="sm:hidden">Plus tard</span>
            </button>
            <button
              type="button"
              data-testid="header-whatsapp-share"
              onClick={shareFormWhatsApp}
              className="flex items-center gap-1.5 rounded-full bg-whatsapp/10 px-3 py-1.5 text-xs font-semibold text-whatsapp-dark transition-colors hover:bg-whatsapp/20"
            >
              <Share2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Partager</span>
            </button>
          </div>
        </div>
        <div className="h-1 w-full bg-slate-100">
          <motion.div
            className="h-full rounded-r-full bg-primary"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>
      </header>

      {/* Body */}
      <main className="mx-auto max-w-[640px] px-5 pb-44 pt-7 sm:pb-32">
        {resumeOffer && (
          <div className="mb-5 flex flex-col gap-3 rounded-xl border border-primary/25 bg-primary/[0.05] px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between" data-testid="resume-offer-banner">
            <div className="flex items-start gap-2 text-sm">
              <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                Vous avez une saisie en cours{resumeOffer.name ? <> ({<span className="font-semibold text-foreground">{resumeOffer.name}</span>})</> : ""}. Souhaitez-vous la reprendre ?
              </span>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                data-testid="resume-continue-button"
                onClick={() => api.get(`/drafts/${resumeOffer.token}`).then((r) => applyDraft(r.data)).catch(() => { toast.error("Saisie introuvable."); localStorage.removeItem(DRAFT_STORAGE_KEY); setResumeOffer(null); })}
                className="flex h-9 items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
              >
                Reprendre
              </button>
              <button
                type="button"
                data-testid="resume-dismiss-button"
                onClick={() => { localStorage.removeItem(DRAFT_STORAGE_KEY); setResumeOffer(null); }}
                className="flex h-9 items-center justify-center rounded-full border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                Recommencer
              </button>
            </div>
          </div>
        )}
        {refName && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/[0.05] px-4 py-3 text-sm" data-testid="ref-banner">
            <BadgeCheck className="h-4 w-4 shrink-0 text-primary" />
            <span>Formulaire transmis par <span className="font-semibold text-foreground">{refName}</span></span>
          </div>
        )}
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            custom={dir}
            initial={{ opacity: 0, x: dir * 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -24 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <SectionHeader icon={Current.icon} title={Current.title} subtitle={Current.subtitle} step={step + 1} total={total} />
            <div className="space-y-5">{step === 0 ? renderDemandeStep(data, set) : renderStep(step - 1, data, set)}</div>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Sticky actions */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-[640px] items-center gap-3 px-5 pb-[84px] pt-3.5 sm:pb-3.5">
          {step > 0 && (
            <button
              type="button"
              data-testid="form-prev-button"
              onClick={() => go(step - 1)}
              className="flex h-12 items-center justify-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Retour
            </button>
          )}
          {step < total - 1 ? (
            <button
              type="button"
              data-testid="form-next-button"
              onClick={handleNext}
              className="flex h-12 flex-1 items-center justify-center gap-1.5 rounded-full bg-primary px-6 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary/90 active:scale-[0.99]"
            >
              Continuer
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="form-submit-button"
              disabled={submitting}
              onClick={handleSubmit}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary/90 active:scale-[0.99] disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitting ? "Envoi…" : "Envoyer ma demande"}
            </button>
          )}
        </div>
      </div>

      <ResumeLinkDialog
        open={resumeDialogOpen}
        onClose={() => setResumeDialogOpen(false)}
        link={resumeLink}
        email={data.email}
        resumeToken={resumeToken}
        onFinishLater={() => { setResumeDialogOpen(false); navigate("/"); }}
      />
    </div>
  );
}

function ResumeLinkDialog({ open, onClose, link, email, resumeToken, onFinishLater }) {
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const message = `Bonjour,\n\nVoici votre lien personnel pour reprendre votre fiche de renseignements Place des Énergies quand vous le souhaitez, sur n'importe quel appareil :\n${link}\n\nÀ très vite,\nPlace des Énergies`;

  const sendWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const openMailClient = () => {
    const subject = encodeURIComponent("Reprendre ma fiche — Place des Énergies");
    window.location.href = `mailto:${email || ""}?subject=${subject}&body=${encodeURIComponent(message)}`;
  };

  const sendEmail = async () => {
    if (resumeToken && email) {
      setSendingEmail(true);
      try {
        const res = await api.post(`/drafts/${resumeToken}/send-email`, { link });
        if (res.data?.sent) {
          toast.success(`Email envoyé à ${res.data.to}. Vérifiez votre boîte de réception.`);
          return;
        }
      } catch {
        // Gmail not connected / no email / send failed -> fall back to the mail client.
      } finally {
        setSendingEmail(false);
      }
    }
    openMailClient();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md max-h-[90vh] overflow-y-auto" data-testid="resume-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Save className="h-5 w-5 shrink-0 text-primary" /> Votre saisie est enregistrée
          </DialogTitle>
          <DialogDescription className="text-sm">
            Envoyez-vous votre lien de reprise pour le retrouver facilement — aucun risque de le perdre. Il fonctionne sur n'importe quel appareil.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-2">
          <button
            type="button"
            onClick={sendWhatsApp}
            data-testid="resume-send-whatsapp"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-whatsapp px-3 text-sm font-semibold text-white transition-colors hover:bg-whatsapp-dark"
          >
            <MessageCircle className="h-4 w-4 shrink-0" /> M'envoyer le lien sur WhatsApp
          </button>
          <button
            type="button"
            onClick={sendEmail}
            disabled={sendingEmail}
            data-testid="resume-send-email"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-primary/30 bg-primary/[0.05] px-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-60"
          >
            {sendingEmail ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Mail className="h-4 w-4 shrink-0" />}
            {email ? "M'envoyer le lien par email" : "Envoyer le lien par email"}
          </button>
        </div>

        <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
          <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
          <p className="min-w-0 flex-1 truncate text-xs text-slate-600" data-testid="resume-link-value">{link}</p>
          <button
            type="button"
            onClick={copy}
            data-testid="resume-link-copy"
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copié" : "Copier"}
          </button>
        </div>

        <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onClose}
            data-testid="resume-continue-now"
            className="flex h-11 flex-1 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
          >
            Continuer maintenant
          </button>
          <button
            type="button"
            onClick={onFinishLater}
            data-testid="resume-finish-later"
            className="flex h-11 flex-1 items-center justify-center rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
          >
            Terminer plus tard
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function renderDemandeStep(data, set) {
  return (
    <>
      <Field label="De quelle demande s'agit-il ?" hint="Mise en service = emménagement / ouverture d'un nouveau compteur.">
        <OptionGrid options={TYPE_DEMANDE} value={data.type_demande} onChange={(v) => set({ type_demande: v })} columns={1} testIdPrefix="type-demande" />
      </Field>
      <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-amber-700">
          <AlertTriangle className="h-4 w-4" /> Votre demande est-elle urgente ?
        </p>
        <OptionGrid options={OUI_NON} value={data.demande_urgente} onChange={(v) => set({ demande_urgente: v })} columns={2} testIdPrefix="demande-urgente" />
      </div>
    </>
  );
}

function renderStep(step, data, set) {
  switch (step) {
    case 0:
      return (
        <>
          <Field label="Apporteur d'affaires (licencié / franchisé)" hint="Optionnel — laissez vide si c'est le particulier qui remplit.">
            <TextInput value={data.apporteur} onChange={(v) => set({ apporteur: v })} testId="input-apporteur" placeholder="Nom du licencié / franchisé" />
          </Field>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Nom" required>
              <TextInput value={data.nom} onChange={(v) => set({ nom: v })} testId="input-nom" placeholder="Dupont" />
            </Field>
            <Field label="Prénom" required>
              <TextInput value={data.prenom} onChange={(v) => set({ prenom: v })} testId="input-prenom" placeholder="Marie" />
            </Field>
          </div>
          <Field label="Téléphone portable" required>
            <TextInput value={data.telephone_portable} onChange={(v) => set({ telephone_portable: v })} testId="input-telephone-portable" type="tel" inputMode="tel" placeholder="06 12 34 56 78" />
          </Field>
          <Field label="Téléphone fixe">
            <TextInput value={data.telephone_fixe} onChange={(v) => set({ telephone_fixe: v })} testId="input-telephone-fixe" type="tel" inputMode="tel" placeholder="01 23 45 67 89" />
          </Field>
          <Field label="Email" required>
            <TextInput value={data.email} onChange={(v) => set({ email: v })} testId="input-email" type="email" inputMode="email" placeholder="marie.dupont@email.com" />
          </Field>
          <Field label="Adresse" required>
            <TextInput value={data.adresse} onChange={(v) => set({ adresse: v })} testId="input-adresse" placeholder="12 rue de la Paix" />
          </Field>
          <Field label="Code postal / Ville" required>
            <TextInput value={data.code_postal_ville} onChange={(v) => set({ code_postal_ville: v })} testId="input-cp-ville" placeholder="75002 Paris" />
          </Field>
          <Field label="Date de naissance">
            <TextInput value={data.date_naissance} onChange={(v) => set({ date_naissance: v })} testId="input-date-naissance" type="date" />
          </Field>
          <div className="space-y-4 rounded-xl border border-primary/20 bg-primary/[0.04] p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Clock className="h-4 w-4" /> Quand souhaitez-vous être rappelé(e) ?
            </p>
            <Field label="Créneau souhaité">
              <OptionGrid options={RAPPEL_CRENEAUX} value={data.rappel_creneau} onChange={(v) => set({ rappel_creneau: v })} columns={2} testIdPrefix="rappel-creneau" />
            </Field>
            <Field label="Date souhaitée (optionnel)">
              <TextInput value={data.rappel_date} onChange={(v) => set({ rappel_date: v })} testId="input-rappel-date" type="date" />
            </Field>
          </div>
        </>
      );
    case 1:
      return (
        <>
          <Field label="Type de compteur">
            <OptionGrid options={ELEC_COMPTEUR} value={data.elec_compteur} onChange={(v) => set({ elec_compteur: v })} columns={2} testIdPrefix="elec-compteur" />
          </Field>
          <Field label="Options (plusieurs choix possibles)">
            <OptionGrid options={ELEC_OPTIONS} value={data.elec_options} onChange={(v) => set({ elec_options: v })} multi columns={2} testIdPrefix="elec-option" />
          </Field>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Puissance">
              <TextInput value={data.elec_puissance} onChange={(v) => set({ elec_puissance: v })} testId="input-elec-puissance" placeholder="ex : 9 kVA" />
            </Field>
            <Field label="Fournisseur actuel">
              <TextInput value={data.elec_fournisseur} onChange={(v) => set({ elec_fournisseur: v })} testId="input-elec-fournisseur" placeholder="ex : EDF" />
            </Field>
          </div>
          <Field label="Numéro de Point de Livraison (PDL)">
            <TextInput value={data.elec_pdl} onChange={(v) => set({ elec_pdl: v })} testId="input-elec-pdl" inputMode="numeric" placeholder="14 chiffres" />
          </Field>
        </>
      );
    case 2:
      return (
        <>
          <Field label="Type de gaz (plusieurs choix possibles)">
            <OptionGrid options={GAZ_TYPE} value={data.gaz_type} onChange={(v) => set({ gaz_type: v })} multi columns={2} testIdPrefix="gaz-type" />
          </Field>
          <Field label="Fournisseur actuel">
            <TextInput value={data.gaz_fournisseur} onChange={(v) => set({ gaz_fournisseur: v })} testId="input-gaz-fournisseur" placeholder="ex : Engie" />
          </Field>
        </>
      );
    case 3:
      return (
        <>
          <Field label="Type de logement">
            <OptionGrid options={LOGEMENT_TYPE} value={data.logement_type} onChange={(v) => set({ logement_type: v })} columns={2} testIdPrefix="logement-type" />
          </Field>
          <Field label="Année de construction">
            <OptionGrid options={ANNEE_CONSTRUCTION} value={data.annee_construction} onChange={(v) => set({ annee_construction: v })} columns={2} testIdPrefix="annee-construction" />
          </Field>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Surface (m²)">
              <TextInput value={data.surface} onChange={(v) => set({ surface: v })} testId="input-surface" type="number" inputMode="numeric" placeholder="ex : 90" />
            </Field>
            <Field label="Nombre d'occupants">
              <TextInput value={data.nombre_occupants} onChange={(v) => set({ nombre_occupants: v })} testId="input-occupants" type="number" inputMode="numeric" placeholder="ex : 4" />
            </Field>
          </div>
          <Field label="Présence dans le logement">
            <OptionGrid options={PRESENCE} value={data.presence} onChange={(v) => set({ presence: v })} testIdPrefix="presence" />
          </Field>
          <Field label="Connaissez-vous le DPE de votre logement ?">
            <OptionGrid options={DPE_CONNU} value={data.connait_dpe} onChange={(v) => set({ connait_dpe: v })} columns={2} testIdPrefix="dpe-connu" />
          </Field>
          {data.connait_dpe === "Oui" && (
            <Field label="Classe DPE">
              <Pills options={DPE_CLASSES} value={data.dpe_classe} onChange={(v) => set({ dpe_classe: v })} testIdPrefix="dpe-classe" />
            </Field>
          )}
        </>
      );
    case 4:
      return (
        <Field label="Mode(s) de chauffage (plusieurs choix possibles)">
          <OptionGrid options={CHAUFFAGE} value={data.chauffage} onChange={(v) => set({ chauffage: v })} multi columns={2} testIdPrefix="chauffage" />
        </Field>
      );
    case 5:
      return (
        <Field label="Production d'eau chaude (plusieurs choix possibles)">
          <OptionGrid options={EAU_CHAUDE} value={data.eau_chaude} onChange={(v) => set({ eau_chaude: v })} multi columns={2} testIdPrefix="eau-chaude" />
        </Field>
      );
    case 6:
      return (
        <Field label="Mode de cuisson (plusieurs choix possibles)">
          <OptionGrid options={CUISSON} value={data.cuisson} onChange={(v) => set({ cuisson: v })} multi columns={2} testIdPrefix="cuisson" />
        </Field>
      );
    case 7:
      return (
        <>
          <Field label="Équipements (plusieurs choix possibles)">
            <OptionGrid options={EQUIPEMENTS} value={data.equipements} onChange={(v) => set({ equipements: v })} multi columns={2} testIdPrefix="equipement" />
          </Field>
          <Field label="Autres équipements">
            <TextInput value={data.equipements_autres} onChange={(v) => set({ equipements_autres: v })} testId="input-equipements-autres" placeholder="Précisez si besoin" />
          </Field>
        </>
      );
    case 8:
      return (
        <>
          <Field label="Capture d'écran de vos prix TTC" hint="Abonnement et prix du kWh, disponibles dans votre espace client." required>
            <PriceUpload value={data.prix_fichier} onChange={(v) => set({ prix_fichier: v })} />
          </Field>
          <Field label="Commentaires">
            <TextArea value={data.commentaires} onChange={(v) => set({ commentaires: v })} testId="input-commentaires" placeholder="Une remarque, une question, un besoin particulier…" rows={5} />
          </Field>
        </>
      );
    case 9:
      return (
        <>
          <button
            type="button"
            data-testid="autorisation-checkbox"
            onClick={() => set({ autorisation_donnees: !data.autorisation_donnees })}
            className={
              "flex w-full items-start gap-3 rounded-xl border-2 p-4 text-left transition-all " +
              (data.autorisation_donnees
                ? "border-primary bg-primary/[0.06] ring-2 ring-primary/10"
                : "border-border bg-white hover:border-primary/40")
            }
          >
            <span
              className={
                "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 " +
                (data.autorisation_donnees ? "border-primary bg-primary text-white" : "border-slate-300 text-transparent")
              }
            >
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <span className="text-sm font-medium text-slate-700">
              J'autorise l'accès à mes données de consommation auprès d'Enedis / GRDF afin que Place des Énergies
              puisse étudier mon dossier.
            </span>
          </button>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-3 font-heading text-base font-bold text-foreground">Récapitulatif</h3>
            <dl className="space-y-2 text-sm">
              <Row label="Type de demande" value={data.type_demande || "—"} />
              <Row label="Demande urgente" value={data.demande_urgente || "—"} />
              <Row label="Nom complet" value={`${data.prenom} ${data.nom}`.trim() || "—"} />
              <Row label="Téléphone" value={data.telephone_portable || "—"} />
              <Row label="Rappel souhaité" value={[data.rappel_creneau, data.rappel_date].filter(Boolean).join(" · ") || "—"} />
              <Row label="Email" value={data.email || "—"} />
              <Row label="Ville" value={data.code_postal_ville || "—"} />
              <Row label="Logement" value={data.logement_type || "—"} />
              <Row label="Pièce jointe" value={data.prix_fichier?.filename || "Aucune"} />
            </dl>
          </div>
          <p className="text-center text-xs text-muted-foreground">
            En envoyant, notre conseillère vous recontacte sous 48h ouvrées.
          </p>
        </>
      );
    default:
      return null;
  }
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="truncate text-right font-medium text-foreground">{value}</dd>
  </div>
);
